import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from "axios";
import globalMU from "globalMU";
import { ErroDaApi } from "./ErroDaApi";
import { ErroDaApiEnterprise } from "./ErroDaApiEnterprise";

const MENSAGEM_SESSAO_EXPIRADA_PADRAO = "A sua sessão do aplicativo expirou. Por favor realize o login novamente.";

// Confirmado com Playwright + expiração real de sessão no banco (playwright-tests/
// investigacaoSessaoExpiradaReal_PA_491123.js): endpoints via MicroUniverso.Rest (ex:
// microuniversoApi.Seguranca.*) respondem, numa sessão expirada, com status de erro HTTP
// (403/500) e corpo {ExceptionType, ExceptionMessage} (padrão HttpError do Web API) — diferente
// do padrão MVC {Error, ErrorType} que globalMU.tratarRetorno já trata, mas que só é lido no
// caminho de SUCESSO (2xx) do interceptor abaixo. Uma resposta de erro é uma REJEIÇÃO do axios;
// sem este onRejected, a Promise rejeitava com um AxiosError cru — não instância de ErroDaApi —
// e tratamentoErroPadrao.ts mostrava "Houve um erro desconhecido" em vez da mensagem real.
function obterDadosSessaoExpirada(error: AxiosError<any>): { mensagem: string } | null {
  const data = error.response?.data;
  if (!data) return null;

  const tipo = data.ExceptionType ?? data.ErrorType;
  if (tipo === "MicroUniverso.Core.Exceptions.SessionExpiredException" || tipo === "SessionExpired" || tipo === "SessionExpiredException") {
    return { mensagem: data.ExceptionMessage ?? data.Error ?? MENSAGEM_SESSAO_EXPIRADA_PADRAO };
  }
  return null;
}

function normalizarErroEnterprise(error: AxiosError<any>): ErroDaApiEnterprise {
  const data = error.response?.data;
  return {
    codigoErro: data?.codigoErro,
    mensagem: data?.mensagem ?? "Erro desconhecido na API Enterprise.",
    status: error.response?.status,
  };
}

export class BaseApi {
    protected api: AxiosInstance;
    protected Enterprise: AxiosInstance;

    constructor() {

        this.api = axios.create({
          // Sem este header, HttpRequestBase.IsAjaxRequest() no servidor (AuthorizeAttribute.cs)
          // retorna false para chamadas via axios (jQuery seta isso automaticamente, axios não).
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        this.api.interceptors.request.use((config) => {
          const abaId = sessionStorage.getItem('aba_id');
          if (abaId) {
            config.headers['XAbaId'] = abaId;
            console.log('[Axios] Header adicionado:', abaId);
          }
          return config;
        });

        this.api.interceptors.response.use(
            response => {
                if ((response.request.responseURL as string)?.split('?')[0]?.endsWith("/Vendedor/Autenticar")) {
                    return { ...response, data: { Unauthorized: "Sem vendedor autentificado." } };
                }

                const mostrarMensagensUsuario = (response.config as AxiosRequestConfig & any).mostrarMensagensUsuario ?? true
                response.data = globalMU.tratarRetorno(response.data, mostrarMensagensUsuario)

                return response
            },
            (error: AxiosError<any>) => {
                const sessaoExpirada = obterDadosSessaoExpirada(error)
                if (sessaoExpirada) {
                    globalMU.showErrorMessage(sessaoExpirada.mensagem)
                    setTimeout(() => globalMU.redirectToLoginPage(), 500)
                    return Promise.reject(new ErroDaApi({ Error: sessaoExpirada.mensagem, ErrorType: "SessionExpired" as any }))
                }

                return Promise.reject(error)
            }
        )

    this.Enterprise = axios.create();
    this.Enterprise.interceptors.response.use(
      (response) => response,
      (error) => {

        if (!axios.isAxiosError(error)) {
          return Promise.reject<ErroDaApiEnterprise>({
            mensagem: "Erro desconhecido na API Enterprise.",
          });
        }

        if (error.response?.status === 404) {
            return Promise.reject<ErroDaApiEnterprise>({
            codigoErro: "404",
            mensagem: `Recurso não encontrado na API Enterprise. ${error.config?.method?.toUpperCase() ?? "desconhecido"} -${error.config?.url ?? "desconhecida"}`,
            status: 404,
            });
        }

        const err = normalizarErroEnterprise(error);
        return Promise.reject(err);
      }
    );
  }
}
